// TrackerLogCoalescer. Collapses a burst of same-(player, key) tracker
// writes into a single logbook entry. Each push() schedules a flush in
// `idleMs`; another push within the window extends the timer and folds
// into the same burst. When the window elapses (or flushAll is called),
// the coalescer emits one snapshot per active burst.
//
// Network sync of each individual tracker write is NOT affected. The
// caller still broadcasts every increment so opponent HUDs animate
// live. Only the logbook entry is debounced.

const DEFAULT_IDLE_MS = 500;

export class TrackerLogCoalescer {
  constructor({ idleMs = DEFAULT_IDLE_MS, emit, now = () => Date.now() } = {}) {
    if (typeof emit !== 'function') {
      throw new Error('TrackerLogCoalescer requires an emit callback');
    }
    this.idleMs = idleMs;
    this.emit = emit;
    this.now = now;
    // Map<bucketKey, { player, key, actor, prev, next, timer }>
    this.bursts = new Map();
  }

  static _bucketKey(player, key, actor) {
    return `${actor}|${player}|${key}`;
  }

  // Record one increment. `prev` is the value BEFORE this write, `next`
  // is AFTER. `actor` is the same string passed to addLogEntry ('you',
  // 'opponent', etc.) so own and remote bursts stay in separate bursts.
  push({ player, key, prev, next, actor }) {
    const bucketKey = TrackerLogCoalescer._bucketKey(player, key, actor);
    const existing = this.bursts.get(bucketKey);
    if (existing) {
      clearTimeout(existing.timer);
      existing.next = next;
      existing.timer = setTimeout(() => this._flushBucket(bucketKey), this.idleMs);
      return;
    }
    const burst = { player, key, actor, prev, next, timer: null };
    burst.timer = setTimeout(() => this._flushBucket(bucketKey), this.idleMs);
    this.bursts.set(bucketKey, burst);
  }

  _flushBucket(bucketKey) {
    const burst = this.bursts.get(bucketKey);
    if (!burst) return;
    clearTimeout(burst.timer);
    this.bursts.delete(bucketKey);
    const delta = burst.next - burst.prev;
    if (delta === 0) return; // net no-op, no log line
    this.emit({
      player: burst.player,
      key: burst.key,
      prev: burst.prev,
      next: burst.next,
      delta,
      actor: burst.actor,
    });
  }

  // Drain all pending bursts immediately. Call before turn-pass so the
  // log lines land before any subsequent end-of-turn entries.
  flushAll() {
    const keys = Array.from(this.bursts.keys());
    for (const k of keys) this._flushBucket(k);
  }

  // Drop pending bursts without emitting (used on match end / scene
  // teardown so stale timers don't fire into a torn-down GameBoard).
  dispose() {
    for (const burst of this.bursts.values()) clearTimeout(burst.timer);
    this.bursts.clear();
  }
}
